// Deterministic conflict fingerprint.
//
// Two detections of the "same" conflict (same kind + same dancers + same
// dances) produce the same fingerprint, even if their sequence_indexes or
// computed_start_times have shifted. This lets the UI match a current
// detection against a `conflict_overrides` row and either suppress the
// warning ("admin already accepted this") or surface it normally.
//
// Format: SHA-256 over `kind || \x1F || sorted_dancer_ids.join(',') ||
// \x1F || sorted_dance_ids.join(',')` returned as 64-char hex.

import type { LineupConflict } from './conflicts';

const SEP = String.fromCharCode(0x1f); // ASCII Unit Separator

async function sha256Hex(input: string): Promise<string> {
  // Use Web Crypto, available in browsers + Deno + Node 20+.
  const enc = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function conflictFingerprint(conflict: LineupConflict): Promise<string> {
  const dancerKey = [...conflict.dancer_ids].sort().join(',');
  const danceKey = [...conflict.dance_ids].sort().join(',');
  return sha256Hex(`${conflict.kind}${SEP}${dancerKey}${SEP}${danceKey}`);
}

// Batch helper — returns a Map<fingerprint, conflict>. Uses Promise.all
// so SHA-256 computations run in parallel; the sequential await loop was
// adding measurable latency for productions with many conflicts.
export async function fingerprintConflicts(
  conflicts: LineupConflict[],
): Promise<Map<string, LineupConflict>> {
  const entries = await Promise.all(
    conflicts.map(async (c) => [await conflictFingerprint(c), c] as const),
  );
  return new Map(entries);
}

// Sync fingerprint variant for the Edge Function / non-async callers that
// can't use the WebCrypto API. Falls back to a fast non-cryptographic
// hash — sufficient because the fingerprint is not a security boundary
// (the override row is RLS-protected; the fingerprint just needs to be
// collision-resistant enough that distinct conflicts don't share keys).
//
// Implementation: FNV-1a 64-bit, then hex-encoded to 16 chars. NOT
// equivalent to the SHA-256 fingerprint — only use this where the async
// variant isn't available, and convert before persisting.
export function fastFingerprint(conflict: LineupConflict): string {
  const dancerKey = [...conflict.dancer_ids].sort().join(',');
  const danceKey = [...conflict.dance_ids].sort().join(',');
  const input = `${conflict.kind}${SEP}${dancerKey}${SEP}${danceKey}`;
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * prime) & 0xffffffffffffffffn;
  }
  return hash.toString(16).padStart(16, '0');
}
